import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const moveCircle1 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(30px, -40px); }
  50% { transform: translate(-20px, -30px); }
  75% { transform: translate(40px, 20px); }
`;

const moveCircle2 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(-40px, 20px); }
  50% { transform: translate(30px, 40px); }
  75% { transform: translate(-30px, -40px); }
`;

const moveCircle3 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, 50px); }
  50% { transform: translate(-40px, 30px); }
  75% { transform: translate(40px, -20px); }
`;

const serviceOfferings = [
  {
    icon: "📋",
    title: "IT Strategy & Roadmap",
    desc: "Develop comprehensive technology strategies aligned with your business goals and future growth objectives."
  },
  {
    icon: "🔍",
    title: "Digital Transformation",
    desc: "Guide your organization through digital transformation journey with proven methodologies and best practices."
  },
  {
    icon: "🏗️",
    title: "Enterprise Architecture",
    desc: "Design scalable, secure, and efficient IT infrastructure to support business operations and innovation."
  },
  {
    icon: "📊",
    title: "IT Governance & Risk",
    desc: "Establish IT governance frameworks, compliance standards, and risk management protocols for your organization."
  },
  {
    icon: "💼",
    title: "Technology Assessment",
    desc: "Evaluate current IT systems, identify gaps, and recommend optimal technology solutions for improvement."
  },
  {
    icon: "🚀",
    title: "Change Management",
    desc: "Support organizational change through strategic planning, training, and communication management."
  }
];

const whyChooseUs = [
  {
    icon: "🎯",
    title: "Strategic Expertise",
    desc: "Decades of combined experience in IT strategy, digital transformation, and enterprise solutions delivery."
  },
  {
    icon: "💡",
    title: "Business-Aligned Solutions",
    desc: "Consulting focused on understanding your business first, then recommending technology that drives results."
  },
  {
    icon: "🔒",
    title: "Risk Mitigation",
    desc: "Identify and address technology risks proactively with comprehensive security and compliance strategies."
  },
  {
    icon: "📈",
    title: "ROI-Focused Approach",
    desc: "Every recommendation is evaluated for measurable business impact and return on investment."
  }
];

const consultingExpertise = [
  {
    icon: "🗺️",
    title: "Strategic Planning",
    desc: "Multi-year IT roadmaps aligned with business strategy and market opportunities."
  },
  {
    icon: "☁️",
    title: "Cloud Strategy",
    desc: "Migration planning, cloud adoption strategies, and multi-cloud architecture design."
  },
  {
    icon: "🛡️",
    title: "Cybersecurity Strategy",
    desc: "Comprehensive security assessments, compliance frameworks, and incident response planning."
  },
  {
    icon: "💰",
    title: "Cost Optimization",
    desc: "IT budget optimization, vendor management, and infrastructure efficiency improvements."
  },
  {
    icon: "👥",
    title: "Organizational Design",
    desc: "Build high-performing IT organizations with optimal skills, roles, and structures."
  }
];

const ITConsultingStrategy = () => {
  useDocumentTitle("Briozon - IT Consulting & Strategy");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  IT CONSULTING & STRATEGY
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Strategic IT{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Guidance
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Transform your IT capabilities into strategic business advantages. Our consulting experts help you align technology investments with business goals, navigate digital transformation, and build resilient IT organizations for sustainable growth.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  💡
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🎯</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Strategy
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🏗️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Architecture
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>☁️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Cloud Strategy
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🔒</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Security
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            {/* Left - Content */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 60,
                  height: 4,
                  background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
                  borderRadius: "2px",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out`,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out 0.1s both`,
                }}
              >
                Strategic IT{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Consulting Services
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                We deliver strategic IT consulting that aligns technology with business objectives. Our experts assess current IT landscapes, identify opportunities for optimization, and develop comprehensive strategies for digital transformation, cloud adoption, and infrastructure modernization.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                  animation: `${fadeInUp} 0.8s ease-out 0.3s both`,
                }}
              >
                From enterprise architecture design to IT governance and risk management, we help organizations build resilient, scalable technology foundations. Our strategic guidance ensures your IT investments deliver maximum value while maintaining security, compliance, and operational excellence across all business functions.
              </Typography>
            </Grid>

            {/* Right - Key Focus Areas */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: { xs: "18px", sm: "20px" } }}
                >
                  Key Focus Areas
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {["Strategy", "Architecture", "Cloud", "Security", "Governance", "Transformation"].map((tech) => (
                    <Box
                      key={tech}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: "30px",
                        background: "#fff",
                        color: "#5b7cfa",
                        fontWeight: 600,
                        fontSize: { xs: "14px", sm: "15px" },
                        border: "1.5px solid rgba(91, 124, 250, 0.3)",
                        boxShadow: "0 2px 8px rgba(91, 124, 250, 0.08)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                          color: "#fff",
                          borderColor: "transparent",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(91, 124, 250, 0.25)",
                        },
                      }}
                    >
                      {tech}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Expertise Section - Alternating Layout */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          {/* Row 1: Our Consulting Expertise */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Our Consulting{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Expertise
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                Briozon specializes in comprehensive IT consulting that transforms technology into competitive advantage. We combine strategic thinking with hands-on implementation expertise to help organizations navigate complexity and achieve their most ambitious business objectives.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.06) 0%, rgba(118,75,162,0.06) 100%)",
                  border: "1px solid rgba(91,124,250,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "🗺️", label: "Multi-Year IT Roadmaps" },
                  { icon: "☁️", label: "Cloud Adoption Strategies" },
                  { icon: "🔒", label: "Cybersecurity Frameworks" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Row 2: Guiding Digital Excellence */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(118,75,162,0.06) 0%, rgba(91,124,250,0.06) 100%)",
                  border: "1px solid rgba(118,75,162,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "💼", label: "Trusted Technology Advisors" },
                  { icon: "📊", label: "Data-Driven Decisions" },
                  { icon: "🚀", label: "Proven Execution Methodologies" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(118,75,162,0.12), rgba(91,124,250,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Guiding Digital{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Excellence
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                }}
              >
                We work as trusted advisors to help you make informed technology decisions. Our consulting approach is collaborative, data-driven, and focused on delivering sustainable business outcomes.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                Whether you're planning a cloud migration, building a new IT organization, or implementing governance frameworks, we bring strategic perspective combined with proven execution methodologies. Our goal is to position your organization for long-term technology leadership and competitive advantage in an increasingly digital world.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Consulting Expertise Cards */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our Consulting{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Services
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              End-to-end consulting services to guide your IT strategy and transformation journey.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {consultingExpertise.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .expertise-icon": {
                      transform: "scale(1.12)",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.25), rgba(118,75,162,0.25))",
                    },
                    "& .expertise-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Box
                  className="expertise-icon"
                  sx={{
                    width: { xs: "52px", sm: "58px" },
                    height: { xs: "52px", sm: "58px" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                    fontSize: { xs: "26px", sm: "30px" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  className="expertise-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1.5,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 1,
              }}
            >
              Why Choose Briozon for{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                IT Consulting
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    borderRadius: "16px",
                    backgroundColor: "#fafbff",
                    border: "1px solid #e8eaf0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "52px", sm: "58px" },
                      height: { xs: "52px", sm: "58px" },
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: "26px", sm: "30px" },
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1b1f33",
                        mb: 1,
                        fontSize: { xs: "20px", sm: "22px" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#4c5472",
                        lineHeight: 1.7,
                        fontSize: { xs: "16px", sm: "17px" },
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Offerings Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our Consulting{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Solutions
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Comprehensive consulting services addressing your most critical IT and business challenges.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {serviceOfferings.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .offering-icon": {
                      transform: "scale(1.12)",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.25), rgba(118,75,162,0.25))",
                    },
                    "& .offering-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Box
                  className="offering-icon"
                  sx={{
                    width: { xs: "52px", sm: "58px" },
                    height: { xs: "52px", sm: "58px" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                    fontSize: { xs: "26px", sm: "30px" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  className="offering-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready for Strategic IT Guidance?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Let's discuss how strategic IT consulting can accelerate your business transformation. Schedule a consultation with our experts to explore technology roadmaps, digital strategy, and organizational optimization.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ITConsultingStrategy;
